import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Excalidraw, reconcileElements, exportToBlob } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import { ArrowLeft, ExternalLink, Trash2, Download, Loader2, Check, AlertTriangle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

// A shared whiteboard: one Excalidraw scene per canvas, saved whole.
//
// Honest about what it is. Saves are last-writer-wins on a version counter,
// with per-element reconciliation when a save collides with someone else's
// (Excalidraw's own reconcileElements: newer element version wins, so two
// people editing different shapes both keep their work). There are no live
// cursors and no keystroke-level sync -- that needs a CRDT document server,
// which is a separate system.

if (typeof window !== "undefined" && !window.EXCALIDRAW_ASSET_PATH) {
  window.EXCALIDRAW_ASSET_PATH = "/excalidraw/";
}

const SAVE_DEBOUNCE_MS = 1500;

// Excalidraw normalises every element on mount -- filling in defaults, assigning
// the fractional index it uses for z-order -- and bumps each element's `version`
// while doing it. A signature built from `version` therefore reports a change
// for a scene nobody touched, which is why opening a whiteboard used to save
// one. With last-writer-wins that is not a spare version bump: it is the opener
// overwriting whatever changed since their own client loaded the scene.
//
// So the signature is the drawing, minus the fields that move on their own. Not
// a whitelist of "interesting" properties -- everything except these, so an edit
// this file has never heard of still counts as an edit.
const VOLATILE = new Set(["version", "versionNonce", "updated", "index", "seed"]);
const signature = (elements) => JSON.stringify(
  (elements || []).map((e) =>
    Object.keys(e).filter((k) => !VOLATILE.has(k)).sort()
      .reduce((acc, k) => { acc[k] = e[k]; return acc; }, {})));

const pref = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };

export default function CanvasPage({ canvasId, workspaceId, currentUser, users = [], myRole = "member", subscribe, onBack, onAskAtena, standalone = false }) {
  const [meta, setMeta] = useState(null);            // everything but the drawing
  const [initial, setInitial] = useState(null);       // the scene handed to Excalidraw once
  const [status, setStatus] = useState("loading");    // loading | saved | dirty | saving | conflict | error
  const [confirmDelete, setConfirmDelete] = useState(false);
  const apiRef = useRef(null);                        // Excalidraw's imperative API
  const versionRef = useRef(0);
  const savedSigRef = useRef("");
  const baselinedRef = useRef(false);   // has the live scene been compared with the loaded one yet
  const timerRef = useRef(null);
  const nameOf = (id) => users.find((u) => u.id === id)?.name || (id === currentUser?.id ? currentUser.name : "un coleg");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/canvases/${canvasId}`);
      const { elements, app_state, files, ...rest } = data;
      setMeta(rest);
      versionRef.current = rest.version || 0;
      savedSigRef.current = signature(elements || []);
      baselinedRef.current = false;
      setInitial({ elements: elements || [], appState: { ...(app_state || {}), theme: pref("vs_theme", "dark") }, files: files || {} });
      setStatus("saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Whiteboard-ul nu s-a putut încărca");
      setStatus("error");
      onBack?.();
    }
  }, [canvasId, onBack]);
  useEffect(() => { load(); }, [load]);

  // Pull the latest scene and merge it into what is on screen, element by element.
  const mergeRemote = useCallback(async () => {
    const ex = apiRef.current;
    if (!ex) return;
    try {
      const { data } = await api.get(`/canvases/${canvasId}`);
      const merged = reconcileElements(ex.getSceneElementsIncludingDeleted(), data.elements || [], ex.getAppState());
      ex.updateScene({ elements: merged });
      if (data.files && Object.keys(data.files).length) ex.addFiles(Object.values(data.files));
      versionRef.current = data.version || 0;
      setMeta((m) => ({ ...(m || {}), version: data.version, updated_at: data.updated_at, updated_by: data.updated_by }));
      // Whatever local edits survived the merge still need to go out.
      const sig = signature(merged);
      setStatus(sig === signature(data.elements || []) ? "saved" : "dirty");
      savedSigRef.current = signature(data.elements || []);
    } catch { /* the next save will retry */ }
  }, [canvasId]);

  const save = useCallback(async () => {
    const ex = apiRef.current;
    if (!ex) return;
    const elements = ex.getSceneElementsIncludingDeleted();
    const sig = signature(elements);
    if (sig === savedSigRef.current) { setStatus("saved"); return; }
    setStatus("saving");
    const appState = ex.getAppState();
    try {
      const { data } = await api.put(`/canvases/${canvasId}/scene`, {
        elements,
        app_state: { viewBackgroundColor: appState.viewBackgroundColor, gridSize: appState.gridSize, gridModeEnabled: appState.gridModeEnabled },
        files: ex.getFiles(),
        base_version: versionRef.current,
      });
      versionRef.current = data.version;
      savedSigRef.current = sig;
      setMeta((m) => ({ ...(m || {}), version: data.version, updated_at: data.updated_at, updated_by: currentUser?.id }));
      setStatus(signature(ex.getSceneElementsIncludingDeleted()) === sig ? "saved" : "dirty");
    } catch (e) {
      if (e?.response?.status === 409) {
        setStatus("conflict");
        toast.info("Un coleg a salvat între timp — am combinat desenele.");
        await mergeRemote();
        // Our surviving edits go out on top of the merged version.
        setTimeout(() => save(), 50);
      } else {
        setStatus("error");
        toast.error(e?.response?.data?.detail || "Nu s-a salvat");
      }
    }
  }, [canvasId, currentUser?.id, mergeRemote]);

  const onChange = useCallback((elements) => {
    if (!apiRef.current) return;
    // Excalidraw fires onChange once as it mounts, before anyone can touch
    // anything. That pass re-baselines against the scene as the editor actually
    // holds it and saves nothing: opening a whiteboard is not an edit, and with
    // last-writer-wins a save that nobody asked for is somebody else's work
    // being thrown away.
    if (!baselinedRef.current) {
      baselinedRef.current = true;
      savedSigRef.current = signature(elements);
      setStatus("saved");
      return;
    }
    if (signature(elements) === savedSigRef.current) return;   // a pan or a selection, not a drawing
    setStatus("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
  }, [save]);

  // Someone else saved: merge their scene into ours.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((ev) => {
      if (ev.type === "canvas_scene" && ev.canvas_id === canvasId && ev.updated_by !== currentUser?.id) mergeRemote();
      else if (ev.type === "canvas_upsert" && ev.canvas?.id === canvasId) setMeta((m) => ({ ...(m || {}), ...ev.canvas }));
      else if (ev.type === "canvas_deleted" && ev.canvas_id === canvasId) { toast.info("Whiteboard-ul a fost șters"); onBack?.(); }
    });
  }, [subscribe, canvasId, currentUser?.id, mergeRemote, onBack]);

  // Leave nothing behind: a pending debounce is flushed on unmount.
  useEffect(() => () => { clearTimeout(timerRef.current); }, []);
  useEffect(() => {
    const before = (e) => { if (status === "dirty" || status === "saving") { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [status]);

  const rename = async (name) => {
    try { const { data } = await api.patch(`/canvases/${canvasId}`, { name }); setMeta((m) => ({ ...m, ...data })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a redenumit"); }
  };
  const remove = async () => {
    try { await api.delete(`/canvases/${canvasId}`); onBack?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a șters"); }
  };
  const exportPng = async () => {
    const ex = apiRef.current;
    if (!ex) return;
    try {
      const blob = await exportToBlob({ elements: ex.getSceneElements(), appState: ex.getAppState(), files: ex.getFiles(), mimeType: "image/png", exportPadding: 24 });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${meta?.name || "whiteboard"}.png`; a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Exportul a eșuat"); }
  };
  const openWindow = () => window.open(`${window.location.origin}/?canvas=${canvasId}&window=1`, "_blank", "noopener,width=1400,height=900");
  const canDelete = myRole === "owner" || myRole === "admin" || meta?.created_by === currentUser?.id;

  const statusLine = useMemo(() => {
    switch (status) {
      case "saved": return { icon: <Check className="w-3 h-3" />, text: `Salvat · v${meta?.version ?? 0}` };
      case "saving": return { icon: <Loader2 className="w-3 h-3 animate-spin" />, text: "Se salvează…" };
      case "dirty": return { icon: null, text: "Modificări nesalvate" };
      case "conflict": return { icon: <AlertTriangle className="w-3 h-3 text-amber-500" />, text: "Se combină…" };
      case "error": return { icon: <AlertTriangle className="w-3 h-3 text-red-400" />, text: "Nu s-a salvat" };
      default: return { icon: <Loader2 className="w-3 h-3 animate-spin" />, text: "Se încarcă…" };
    }
  }, [status, meta?.version]);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="canvas-page">
      <div className="min-h-12 px-3 py-1.5 flex items-center gap-3 border-b border-line/10 bg-bg">
        {!standalone && <button type="button" onClick={onBack} className="p-1.5 text-fg/50 hover:text-fg" aria-label="Înapoi la whiteboard-uri"><ArrowLeft className="w-4 h-4" /></button>}
        {editingName ? (
          <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => { setEditingName(false); const v = nameDraft.trim(); if (v && v !== meta?.name) rename(v); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingName(false); }}
            className="font-bold text-base bg-surface border border-brand/50 px-2 py-0.5 outline-none" />
        ) : (
          <button type="button" data-testid="canvas-title" onClick={() => { setNameDraft(meta?.name || ""); setEditingName(true); }} className="font-bold tracking-tight text-base hover:text-brand truncate max-w-[40vw]">
            {meta?.name || "…"}
          </button>
        )}
        <span data-testid="canvas-status" className="text-[11px] text-fg/50 mono flex items-center gap-1">{statusLine.icon}{statusLine.text}</span>
        {meta?.updated_by && meta.updated_by !== currentUser?.id && status === "saved" && (
          <span className="text-[11px] text-fg/40">ultima dată de {nameOf(meta.updated_by)}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {onAskAtena && <button type="button" data-testid="canvas-ask-atena" onClick={onAskAtena} className="p-1.5 border border-line/20 text-fg/60 hover:text-brand" title="Întreabă Atena despre whiteboard-ul acesta"><Sparkles className="w-3.5 h-3.5" /></button>}
          <button type="button" onClick={exportPng} className="p-1.5 border border-line/20 text-fg/60 hover:text-fg" title="Export PNG"><Download className="w-3.5 h-3.5" /></button>
          {!standalone && <button type="button" data-testid="open-window" onClick={openWindow} className="p-1.5 border border-line/20 text-fg/60 hover:text-fg" title="Deschide în fereastră separată"><ExternalLink className="w-3.5 h-3.5" /></button>}
          {canDelete && <button type="button" onClick={() => setConfirmDelete(true)} className="p-1.5 border border-line/20 text-fg/40 hover:text-red-400" title="Șterge whiteboard-ul"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
      <div className="flex-1 min-h-0" data-testid="canvas-surface">
        {initial ? (
          <Excalidraw
            excalidrawAPI={(a) => { apiRef.current = a; }}
            initialData={initial}
            onChange={onChange}
            theme={initial.appState.theme === "light" ? "light" : "dark"}
            langCode="ro-RO"
            UIOptions={{ canvasActions: { loadScene: false, saveToActiveFile: false, export: false, saveAsImage: true, clearCanvas: true, changeViewBackgroundColor: true, toggleTheme: false } }}
          />
        ) : (
          <div className="p-6 text-sm text-fg/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă…</div>
        )}
      </div>
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title="Ștergi whiteboard-ul?" description={`«${meta?.name}» dispare pentru toată lumea. Nu se poate anula.`}
        confirmLabel="Șterge" destructive onConfirm={remove} testId="confirm-delete-canvas" />
    </div>
  );
}
